import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const formFields = [
    {
        id: 'company',
        label: 'Название компании',
        type: 'text',
        placeholder: 'ООО "Рускомтехнологии"',
        required: true
    },
    {
        id: 'primary_business',
        label: 'Опишите Ваши основные направления бизнеса',
        type: 'text',
        placeholder: 'Мониторинг',
        required: true
    },
    {
        id: 'market',
        label: 'Вертикали рынка в которых вы работаете',
        type: 'text',
        placeholder: 'Big Data',
        required: true
    },
    {
        id: 'primary_vendors',
        label: 'Ключевые вендоры',
        type: 'text',
        placeholder: 'Microsoft',
        required: true
    },
    {
        id: 'vendor',
        label: 'Вендоры в сегменте решений мониторинга',
        type: 'text',
        placeholder: 'Gatner',
        required: true
    },
    {
        id: 'contact_person',
        label: 'Контактная персона в Вашей организации',
        type: 'text',
        placeholder: 'Зам директора Иванов Иван',
        required: true
    },
    {
        id: 'email',
        label: 'E-mail',
        type: 'email',
        placeholder: 'email@mail.ru',
        required: true
    },
    {
        id: 'additional_information',
        label: 'Дополнительная информация (необязательно)',
        type: 'textarea',
        placeholder: '31 365 Р ***',
        required: false
    }
];

const schema = yup.object().shape({
    company: yup.string().required('Это поле обязательно'),
    primary_business: yup.string().required('Это поле обязательно'),
    market: yup.string().required('Это поле обязательно'),
    vendor: yup.string().required('Это поле обязательно'),
    primary_vendors: yup.string().required('Это поле обязательно'),
    contact_person: yup.string().required('Это поле обязательно'),
    email: yup.string().email('Неверный формат email').required('Это поле обязательно'),
    additional_information: yup.string(),
    repeat_email: yup.string()
});

const FormField = ({ field, register, error }) => {
    const baseInputClasses = "w-full h-[32px] lg:h-[52px] px-[20px] lg:px-[27px] lg:py-3.5 bg-white rounded-[10px] text-[#544a28] text-[13px] lg:text-base font-normal font-['Open Sans']";
    
    return (
        <div className="w-full xl:w-[670px] flex-col justify-start items-start gap-5 inline-flex">
            <label className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                {field.label}
            </label>
            {field.type === 'textarea' ? (
                <textarea
                    {...register(field.id)}
                    placeholder={field.placeholder}
                    className={`${baseInputClasses} pt-[10px] lg:pt-3.5 min-h-[32px] lg:min-h-[52px] resize-y ${error ? 'border-red-500 border-2' : ''}`}
                />
            ) : (
                <input
                    type={field.type}
                    {...register(field.id)}
                    placeholder={field.placeholder}
                    className={`${baseInputClasses} ${error ? 'border-red-500 border-2' : ''}`}
                />
            )}
            {error && <span className="text-red-500 text-sm">{error.message}</span>}
        </div>
    );
};

const PartnerForm = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (formData) => {
        setIsSubmitting(true);
        setSubmitStatus({ type: '', message: '' });
        console.log(formData);

        try {
            const response = await fetch('/api/v1/partner/partner-reg', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Ошибка отправки формы');
            }

            // Success
            setSubmitStatus({
                type: 'success',
                message: 'Форма успешно отправлена!'
            });
            reset(); // Clear the form
        } catch (error) {
            // Error
            setSubmitStatus({
                type: 'error',
                message: error.message || 'Ошибка отправки формы. Пожалуйста, попробуйте снова.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="w-full lg:w-[1024px] xxl:w-[1400px] flex-col justify-center items-center gap-10 inline-flex">
            <div className="h-11 text-center bg-gradient-to-r from-[#6082A0] to-[#E55961] text-transparent bg-clip-text leading-normal text-[24px] lg:text-[32px] font-extrabold lg:font-bold font-['Open Sans']">
                Партнерская форма
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="self-stretch p-5 bg-[#ebebeb] rounded-[20px] flex-col justify-start items-center gap-10 flex">
                {/* Status Message */}
                {submitStatus.message && (
                    <div className={`w-full p-4 rounded-md ${
                        submitStatus.type === 'success' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                    }`}>
                        {submitStatus.message}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input type="hidden" {...register('repeat_email')} />
                    
                    {formFields.map((field) => (
                        <FormField
                            key={field.id}
                            field={field}
                            register={register}
                            error={errors[field.id]}
                        />
                    ))}
                </div>

                <button 
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-10 h-[45px] bg-[#c0834d] rounded-[20px] text-white text-[20px] lg:text-xl font-bold font-['Open Sans'] ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                    {isSubmitting ? 'Отправка...' : 'Отправить'}
                </button>
            </form>
        </div>
    );
};

export default PartnerForm;